import React, { createContext, useState, useEffect } from 'react';
import api from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Load user on mount
  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, []);

  // Load user from token — retries on transient errors, only clears session on auth failures
  const loadUser = () => {
    const attempt = async (retriesLeft) => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data.data.user);
        setLoading(false);
      } catch (error) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          // Definitive auth failure — interceptor already attempted token refresh
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setToken(null);
          setUser(null);
          setLoading(false);
        } else if (retriesLeft > 0) {
          // Transient error (network, 429, 500) — retry rather than logging out
          setTimeout(() => attempt(retriesLeft - 1), 1500);
        } else {
          // All retries exhausted — clear session
          console.error('Failed to load user after retries');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setToken(null);
          setUser(null);
          setLoading(false);
        }
      }
    };
    attempt(2);
  };

  // Login
  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      const { token, refreshToken, user } = res.data.data;

      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      setToken(token);
      setUser(user);

      return { success: true, user };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, message };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setUser(null);
  };

  // Update user
  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'superadmin' || user?.level === 1,
    isBranchAdmin: user?.role === 'branchadmin',
    isStudent: user?.role === 'student',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
